import { c as _c } from "react/compiler-runtime";
import { IcoArrowCircleUp16 } from '@onefootprint/icons';
import type { Entity } from '@onefootprint/request-types/dashboard';
import { InlineAlert, LinkButton } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import { format, isToday, isYesterday } from 'date-fns';
import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AnimatedContainer from '@/components/animated-container';
import useCurrentEntityTimeline from '@/components/entities/components/details/hooks/use-current-entity-timeline';
import TimelineItemRow from '@/components/timeline/components/timeline-item-row/timeline-item-row';
import useShowOnboarding from '../../../onboardings-shared/hooks/use-show-onboarding';
import AuditTrailEmptyState from './components/audit-trail-empty-state';
import AuditTrailTimelineSkeleton from './components/audit-trail-timeline-skeleton';
import useAddIncompleteEvents from './hooks/use-add-incomplete-events';
import useComposeAuditTrailEvents from './hooks/use-compose-audit-trail-events';
import { type AuditTrailScope, scopeToTimelineQuery } from './utils/key-events';
export type AuditTrailTimelineProps = {
  entity: Entity;
  scope: AuditTrailScope;
  onChangeScope: (scope: AuditTrailScope) => void;
};
export type TimelineItem = {
  timestamp?: string;
  iconComponent?: React.ReactNode; // If icon is not provided, we show a dashed line between prev and next items
  headerComponent: React.ReactNode;
  bodyComponent?: React.ReactNode;
  id?: string;
  onboardingId?: string;
  skipDayGrouping?: boolean;
};
type HighlightState = 'none' | 'selected' | 'other_hovered' | 'hovered';
const highlightStateOpacity: Record<HighlightState, string> = {
  none: '0',
  selected: '0.6',
  other_hovered: '0.4',
  hovered: '1.0'
};
const RotatedIcon = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <span className="rotate-180"><IcoArrowCircleUp16 color="accent" /></span>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
const getHighlightState = (args: {
  isHoveredOnboarding: boolean;
  isSelectedOnboarding: boolean;
  isOnlyOneOnboarding: boolean;
  isOtherOnboardingHovered: boolean;
}): HighlightState => {
  const {
    isHoveredOnboarding,
    isSelectedOnboarding,
    isOnlyOneOnboarding,
    isOtherOnboardingHovered
  } = args;
  if (isHoveredOnboarding) return 'hovered';
  if (isSelectedOnboarding && !isOnlyOneOnboarding) return isOtherOnboardingHovered ? 'other_hovered' : 'selected';
  return 'none';
};
const AuditTrailTimeline = t0 => {
  const $ = _c(41);
  const {
    entity,
    scope,
    onChangeScope
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "auditTrail"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("entity-details", t1);
  let t2;
  if ($[1] !== scope) {
    t2 = scopeToTimelineQuery(scope);
    $[1] = scope;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    data: timeline,
    error,
    hasNextPage,
    fetchNextPage,
    isPending,
    count,
    refetch
  } = useCurrentEntityTimeline(t2);
  const {
    count: totalCount
  } = useCurrentEntityTimeline();
  const hiddenCount = scope === "key" && totalCount != null && count != null ? Math.max(totalCount - count, 0) : 0;
  const {
    onboardingId,
    scrollOnboardingIntoView
  } = useShowOnboarding();
  const [hoveredItemIdx, setHoveredItemIdx] = useState(undefined);
  let t3;
  if ($[3] !== timeline) {
    t3 = timeline || [];
    $[3] = timeline;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const allTimelineEvents = useAddIncompleteEvents(t3, entity);
  const items = useComposeAuditTrailEvents(entity, allTimelineEvents);
  let t4;
  if ($[5] !== t) {
    t4 = timestamp => {
      if (!timestamp) {
        return;
      }
      const date = new Date(timestamp);
      if (isToday(date)) {
        return t(_temp);
      }
      if (isYesterday(date)) {
        return t(_temp2);
      }
      return format(date, "MMM d, yyyy");
    };
    $[5] = t;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const dayLabel = t4;
  if (error) {
    let t5;
    if ($[7] !== t) {
      t5 = t(_temp3);
      $[7] = t;
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    let t6;
    if ($[9] !== refetch) {
      t6 = () => refetch();
      $[9] = refetch;
      $[10] = t6;
    } else {
      t6 = $[10];
    }
    let t7;
    if ($[11] !== t5 || $[12] !== t6) {
      t7 = {
        label: t5,
        onClick: t6
      };
      $[11] = t5;
      $[12] = t6;
      $[13] = t7;
    } else {
      t7 = $[13];
    }
    let t8;
    if ($[14] !== t) {
      t8 = t(_temp4);
      $[14] = t;
      $[15] = t8;
    } else {
      t8 = $[15];
    }
    let t9;
    if ($[16] !== t7 || $[17] !== t8) {
      t9 = <InlineAlert variant="error" cta={t7}>{t8}</InlineAlert>;
      $[16] = t7;
      $[17] = t8;
      $[18] = t9;
    } else {
      t9 = $[18];
    }
    return t9;
  }
  if (isPending) {
    let t5;
    if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <AuditTrailTimelineSkeleton />;
      $[19] = t5;
    } else {
      t5 = $[19];
    }
    return t5;
  }
  if (!items.length) {
    if (scope === "key") {
      let t5;
      if ($[20] !== onChangeScope) {
        t5 = <AuditTrailEmptyState onShowAll={() => onChangeScope("all")} />;
        $[20] = onChangeScope;
        $[21] = t5;
      } else {
        t5 = $[21];
      }
      return t5;
    }
    let t5;
    if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <AuditTrailEmptyState />;
      $[22] = t5;
    } else {
      t5 = $[22];
    }
    return t5;
  }
  let t5;
  let t6;
  if ($[23] !== dayLabel || $[24] !== fetchNextPage || $[25] !== hasNextPage || $[26] !== hoveredItemIdx || $[27] !== items || $[28] !== onboardingId || $[29] !== scrollOnboardingIntoView || $[30] !== t) {
    const allItems = [...items];
    if (hasNextPage) {
      allItems.push({
        headerComponent: <LinkButton onClick={() => fetchNextPage()}>{t(_temp5)}</LinkButton>,
        iconComponent: null
      });
    }
    const isOnlyOneOnboarding = new Set(items.filter(_temp6).map(_temp7)).size <= 1;
    const hoveredItem = hoveredItemIdx !== undefined ? allItems[hoveredItemIdx] : undefined;
    const earliestContiguousItemForHoveredOnboarding = () => {
      if (hoveredItemIdx === undefined) {
        return;
      }
      for (let j = hoveredItemIdx; j >= 0; j--) {
        if (allItems[j - 1]?.onboardingId !== hoveredItem?.onboardingId) {
          return allItems[j];
        }
      }
    };
    let lastHeaderDay;
    const dayHeaders = allItems.map(item_1 => {
      if (item_1.skipDayGrouping) {
        return;
      }
      const day = dayLabel(item_1.timestamp);
      if (!day || day === lastHeaderDay) {
        return;
      }
      lastHeaderDay = day;
      return day;
    });
    t5 = "z-0 flex flex-col";
    t6 = allItems.map((item_2, i) => {
      const itemHasOnboardingId = !!item_2.onboardingId;
      const isFirstEventForOnboarding = itemHasOnboardingId && allItems[i - 1]?.onboardingId !== item_2.onboardingId;
      const isLastEventForOnboarding = itemHasOnboardingId && allItems[i + 1]?.onboardingId !== item_2.onboardingId;
      const isEarliestContiguousItem = item_2.id === earliestContiguousItemForHoveredOnboarding()?.id;
      const showViewOnboardingButton = itemHasOnboardingId && isEarliestContiguousItem;
      const isSelectedOnboarding = itemHasOnboardingId && item_2.onboardingId === onboardingId;
      const isHoveredOnboarding = itemHasOnboardingId && item_2.onboardingId === hoveredItem?.onboardingId;
      const isOtherOnboardingHovered = !!hoveredItem?.onboardingId && hoveredItem?.onboardingId !== item_2.onboardingId;
      const highlightState = getHighlightState({
        isHoveredOnboarding,
        isSelectedOnboarding,
        isOnlyOneOnboarding,
        isOtherOnboardingHovered
      });
      const rightActions = <div className="hidden md:contents"><AnimatedContainer isExpanded={showViewOnboardingButton}><LinkButton variant="label-2" onClick={() => scrollOnboardingIntoView(item_2.onboardingId || "")} iconComponent={RotatedIcon} iconPosition="right">{t(_temp8)}</LinkButton></AnimatedContainer></div>;
      const currentDay = dayHeaders[i];
      const showDayHeader = !!currentDay;
      return <Fragment key={item_2.id ?? `${item_2.timestamp ?? "empty"}-${item_2.onboardingId ?? "show-more"}`}>{showDayHeader && <p className={cx("pb-3 text-label-2 text-primary", i > 0 && "pt-6")}>{currentDay}</p>}<TimelineItemRow item={item_2} i={i} allItems={allItems} onMouseLeave={() => setHoveredItemIdx(undefined)} onMouseOver={() => setHoveredItemIdx(i)} rightActions={rightActions} timeOnly={true} hideConnector={!!dayHeaders[i + 1] && !item_2.bodyComponent}><div className={cx("bg-secondary -z-10 transition-opacity duration-200 absolute top-0 -left-3 -right-3", {
            "h-full": !isLastEventForOnboarding || item_2.bodyComponent,
            "h-full md:h-8": isLastEventForOnboarding && !item_2.bodyComponent,
            "rounded-t-default": isFirstEventForOnboarding,
            "rounded-b-default": isLastEventForOnboarding
          })} style={{
            opacity: highlightStateOpacity[highlightState]
          }} /></TimelineItemRow></Fragment>;
    });
    $[23] = dayLabel;
    $[24] = fetchNextPage;
    $[25] = hasNextPage;
    $[26] = hoveredItemIdx;
    $[27] = items;
    $[28] = onboardingId;
    $[29] = scrollOnboardingIntoView;
    $[30] = t;
    $[31] = t5;
    $[32] = t6;
  } else {
    t5 = $[31];
    t6 = $[32];
  }
  let t7;
  if ($[33] !== hiddenCount || $[34] !== onChangeScope || $[35] !== t) {
    t7 = hiddenCount > 0 && <div data-testid="routine-hidden-counter" className="flex items-center gap-1 pt-4"><p className="text-body-3 text-quaternary">{hiddenCount === 1 ? t(_temp9, {
          count: hiddenCount
        }) : t(_temp10, {
          count: hiddenCount
        })}</p><LinkButton variant="label-3" onClick={() => onChangeScope("all")}>{t(_temp11)}</LinkButton></div>;
    $[33] = hiddenCount;
    $[34] = onChangeScope;
    $[35] = t;
    $[36] = t7;
  } else {
    t7 = $[36];
  }
  let t8;
  if ($[37] !== t5 || $[38] !== t6 || $[39] !== t7) {
    t8 = <div className={t5}>{t6}{t7}</div>;
    $[37] = t5;
    $[38] = t6;
    $[39] = t7;
    $[40] = t8;
  } else {
    t8 = $[40];
  }
  return t8;
};
export default AuditTrailTimeline;
function _temp($) {
  return $.day.today;
}
function _temp2($_0) {
  return $_0.day.yesterday;
}
function _temp3($_1) {
  return $_1.tryAgain;
}
function _temp4($_2) {
  return $_2.error;
}
function _temp5($_3) {
  return $_3.showMore;
}
function _temp6(item) {
  return !!item.onboardingId;
}
function _temp7(item_0) {
  return item_0.onboardingId;
}
function _temp8($_4) {
  return $_4.viewOnboarding;
}
function _temp9($_5) {
  return $_5.routineHiddenOne;
}
function _temp10($_6) {
  return $_6.routineHiddenOther;
}
function _temp11($_7) {
  return $_7.showAll;
}